import { Helmet } from 'react-helmet-async'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Unstable_Grid2'
import CardHeader from '@mui/material/CardHeader'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import Box from '@mui/material/Box'
import { format } from 'date-fns'
import { fNumber, fShortenNumber } from 'src/utils/format-number'
import { masks } from 'src/utils/masks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useBoolean } from 'src/hooks/use-boolean'
import axiosInstance from 'src/utils/axios'
import Iconify from 'src/components/iconify'
import Scrollbar from 'src/components/scrollbar'
import DateTimeRangePicker from 'src/components/date-time-range-picker'
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  emptyRows,
  useTable,
} from 'src/components/table'
import { Autocomplete, Table, TableBody, TextField, Skeleton, Tooltip } from '@mui/material'
import useEvents from 'src/service/events.service'
import useDebounce from 'src/hooks/use-debounce'
import { OptionType } from 'src/components/hook-form/rhf-autocomplete'
import Button from '@mui/material/Button'
import Chart, { useChart } from 'src/components/chart'

export default function ProductsAnalysis() {
  const isLoading = useBoolean(false)
  const [query, setQuery] = useState('')
  const [eventFilter, setEventFilter] = useState<any>()
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const searchDebounce = useDebounce(query, 500)

  const [productsData, setProductsData] = useState<any>()

  const { isLoading: loadingEvent, data: dataEvent } = useEvents(
    {
      page: 1,
      perPage: 100,
      sort: undefined,
    },
    {
      search: searchDebounce,
    },
  )

  const fetchProductsData = async (event?: string) => {
    const eventId = event || eventFilter?.value
    if (!eventId) return

    try {
      isLoading.onTrue()
      const { data: response } = await axiosInstance.get(`/dashboard/products-report`, {
        params: {
          event: eventId,
          startDate: startDate ? format(startDate, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx") : undefined,
          endDate: endDate ? format(endDate, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx") : undefined,
        },
      })

      setProductsData(response)
    } catch {
      // ignore
    } finally {
      isLoading.onFalse()
    }
  }

  const handleFilterService = (event: any, selectedValue: any) => {
    setEventFilter(selectedValue)
    if (selectedValue?.value) {
      fetchProductsData(selectedValue.value)
    } else {
      setProductsData(undefined)
    }
  }

  const handleRefresh = () => {
    fetchProductsData()
  }

  useEffect(() => {
    if (eventFilter?.value) {
      fetchProductsData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  return (
    <>
      <Helmet>
        <title>Dashboard: Análise de Produtos</title>
      </Helmet>

      <Container maxWidth='xl'>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: 3,
            p: 4,
            mb: 4,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -30,
              right: -30,
              width: 120,
              height: 120,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            }}
          />
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Stack direction='row' alignItems='center' spacing={2}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Iconify icon='solar:box-minimalistic-bold' width={28} />
              </Box>
              <Box>
                <Typography variant='h3' fontWeight='bold' gutterBottom>
                  Análise de Produtos
                </Typography>
                <Typography variant='body1' sx={{ opacity: 0.9 }}>
                  Faturamento, categorias e ranking de vendas
                </Typography>
              </Box>
            </Stack>

            <Button
              variant='contained'
              color='inherit'
              onClick={handleRefresh}
              disabled={isLoading.value}
              startIcon={
                <Iconify
                  icon='solar:refresh-bold'
                  sx={{
                    animation: isLoading.value ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              }
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              Atualizar
            </Button>
          </Stack>
        </Box>

        <Stack spacing={4}>
          <Card
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(244, 245, 247, 0.8) 0%, white 100%)',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Stack direction='row' alignItems='center' gap={2} mb={3}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Iconify icon='solar:filter-bold' width={20} color='grey.600' />
              </Box>
              <Box>
                <Typography variant='h6' fontWeight='bold' color='text.primary'>
                  Filtros
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Selecione evento e período para análise
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={3}>
              <Grid xs={12}>
                <Autocomplete
                  id='event-list'
                  options={
                    dataEvent?.data?.map((option) => ({
                      label: option?.name,
                      value: option?._id,
                    })) || []
                  }
                  isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
                  getOptionLabel={(option: OptionType | string) => (option as OptionType)?.label}
                  onChange={handleFilterService}
                  value={eventFilter}
                  loading={loadingEvent}
                  inputValue={query}
                  onInputChange={(event, newInputValue) => {
                    setQuery(newInputValue)
                  }}
                  renderOption={(props, option: any) => (
                    <li {...props} key={option.value}>
                      {option.label}
                    </li>
                  )}
                  renderInput={(params) => <TextField label='Evento' {...params} />}
                  sx={{ flex: 1 }}
                />
              </Grid>
              <Grid xs={12}>
                <DateTimeRangePicker
                  label='Período de análise'
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(start, end) => {
                    setStartDate(start)
                    setEndDate(end)
                  }}
                />
              </Grid>
            </Grid>
          </Card>

          {(() => {
            if (!eventFilter?.value) {
              return (
                <Card
                  sx={{
                    p: { xs: 4, md: 8 },
                    borderRadius: 3,
                    textAlign: 'center',
                    background:
                      'linear-gradient(135deg, rgba(16, 185, 129, 0.04) 0%, rgba(5, 150, 105, 0.04) 100%)',
                    border: '2px dashed',
                    borderColor: 'success.light',
                  }}
                >
                  <Iconify
                    icon='solar:chart-2-bold-duotone'
                    width={72}
                    sx={{ color: 'success.main', opacity: 0.6, mb: 2 }}
                  />
                  <Typography variant='h5' gutterBottom color='text.primary'>
                    Selecione um evento
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ maxWidth: 400, mx: 'auto' }}
                  >
                    Escolha um evento no filtro acima para visualizar a análise completa de produtos,
                    categorias e ranking de vendas.
                  </Typography>
                </Card>
              )
            }

            if (isLoading.value) {
              return (
                <>
                  <Grid container spacing={3}>
                    {Array.from(new Array(4)).map((_, index) => (
                      <Grid key={index} xs={12} md={3}>
                        <SkeletonOverviewCard />
                      </Grid>
                    ))}
                  </Grid>
                  <SkeletonBigTableCard />
                  <SkeletonBigTableCard />
                </>
              )
            }

            if (!productsData) return null

            const overview = productsData.overview || {}

            return (
              <>
                <Grid container spacing={3}>
                  <Grid xs={12} md={3}>
                    <OverviewCard
                      title='Faturamento Total'
                      value={masks.currencyAllPlatforms(overview.totalRevenue || 0)}
                      subtitle='receita bruta'
                      icon='solar:wallet-money-bold'
                      color='success'
                    />
                  </Grid>
                  <Grid xs={12} md={3}>
                    <OverviewCard
                      title='Qtd Vendida'
                      value={fNumber(overview.totalQuantitySold || 0)}
                      subtitle='unidades vendidas'
                      icon='solar:bag-smile-bold'
                      color='info'
                    />
                  </Grid>
                  <Grid xs={12} md={3}>
                    <OverviewCard
                      title='Total de Produtos'
                      value={overview.totalProductsCount || 0}
                      subtitle='produtos únicos'
                      icon='solar:box-minimalistic-bold'
                      color='primary'
                    />
                  </Grid>
                  <Grid xs={12} md={3}>
                    <OverviewCard
                      title='Preço Médio'
                      value={masks.currencyAllPlatforms(overview.averagePrice || 0)}
                      subtitle='por unidade vendida'
                      icon='solar:tag-price-bold'
                      color='warning'
                    />
                  </Grid>
                </Grid>

                <SalesByHourCard data={productsData.salesByHour || []} />

                <ProductsByHourHeatmap data={productsData.topProductsByHour || []} />

                <CategoryAnalysisTable data={productsData.categoryAnalysis || []} />

                <ProductRankingTable data={productsData.productRanking || []} />
              </>
            )
          })()}
        </Stack>
      </Container>
    </>
  )
}

function OverviewCard({ title, value, subtitle, icon, color }: any) {
  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: `${color}.light`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          bgcolor: `${color}.main`,
        },
      }}
    >
      <Stack direction='row' alignItems='center' spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: `${color}.main`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Iconify icon={icon} width={24} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant='h4' fontWeight={700} color='text.primary'>
            {value}
          </Typography>
          <Typography variant='subtitle2' color='text.primary'>
            {title}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Card>
  )
}

// ----------------------------------------------------------------------

const CATEGORY_HEAD = [
  { id: 'category', label: 'Categoria', width: 200 },
  { id: 'products', label: 'Produtos', width: 100 },
  { id: 'quantity', label: 'Qtd Vendida', width: 120 },
  { id: 'revenue', label: 'Faturamento', width: 140 },
]

function CategoryAnalysisTable({ data }: { data: any[] }) {
  const table = useTable()

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardHeader
        title={
          <Stack direction='row' alignItems='center' spacing={1}>
            <Iconify icon='solar:archive-minimalistic-bold' width={18} />
            <Typography variant='subtitle1' fontWeight={700}>
              Vendas por Categoria
            </Typography>
          </Stack>
        }
        subheader={`${data.length} categorias`}
        sx={{ py: 1.25, px: 2, '& .MuiCardHeader-subheader': { fontSize: 12 } }}
      />
      <Scrollbar>
        <Table size='small' sx={{ minWidth: 560 }}>
          <TableHeadCustom
            order={table.order}
            orderBy={table.orderBy}
            headLabel={CATEGORY_HEAD}
            rowCount={data.length}
            numSelected={table.selected.length}
            onSort={table.onSort}
            onRequestSort={table.onSort}
            sx={{ '& th': { py: 1, fontSize: 12 } }}
          />
          <TableBody>
            {data.map((category) => (
              <TableRow key={category._id} hover sx={{ '& td': { py: 0.75 } }}>
                <TableCell>
                  <Typography variant='body2' fontWeight={600} fontSize={13}>
                    {category.categoryName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2' fontSize={13}>
                    {category.totalProductsCount}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2' fontWeight={600} fontSize={13}>
                    {fNumber(category.totalQuantitySold)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2' fontWeight={700} color='success.main' fontSize={13}>
                    {masks.currencyAllPlatforms(category.totalRevenue)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            <TableNoData notFound={!data.length} />
          </TableBody>
        </Table>
      </Scrollbar>
    </Card>
  )
}

const PRODUCT_HEAD = [
  { id: 'productName', label: 'Produto', width: 220 },
  { id: 'categoryName', label: 'Categoria', width: 140 },
  { id: 'totalQuantitySold', label: 'Qtd Vendida', width: 110 },
  { id: 'totalRevenue', label: 'Faturamento', width: 140 },
  { id: 'averagePrice', label: 'Preço Médio', width: 120 },
]

function descendingComparator(a: any, b: any, orderBy: string) {
  const valA = a[orderBy] ?? ''
  const valB = b[orderBy] ?? ''
  if (typeof valA === 'string') {
    return valB.localeCompare(valA, 'pt-BR')
  }
  if (valB < valA) return -1
  if (valB > valA) return 1
  return 0
}

function getComparator(order: 'asc' | 'desc', orderBy: string) {
  return order === 'desc'
    ? (a: any, b: any) => descendingComparator(a, b, orderBy)
    : (a: any, b: any) => -descendingComparator(a, b, orderBy)
}

function ProductRankingTable({ data }: { data: any[] }) {
  const table = useTable({
    defaultOrderBy: 'totalRevenue',
    defaultOrder: 'desc',
    defaultRowsPerPage: 10,
  })

  const sorted = useMemo(
    () => [...data].sort(getComparator(table.order, table.orderBy)),
    [data, table.order, table.orderBy],
  )

  const paged = sorted.slice(
    table.page * table.rowsPerPage,
    table.page * table.rowsPerPage + table.rowsPerPage,
  )

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardHeader
        title={
          <Stack direction='row' alignItems='center' spacing={1}>
            <Iconify icon='solar:ranking-bold' width={18} />
            <Typography variant='subtitle1' fontWeight={700}>
              Ranking de Produtos
            </Typography>
          </Stack>
        }
        subheader={`${data.length} produtos • clique no cabeçalho para ordenar`}
        sx={{ py: 1.25, px: 2, '& .MuiCardHeader-subheader': { fontSize: 12 } }}
      />
      <Scrollbar>
        <Table size='small' sx={{ minWidth: 720 }}>
          <TableHeadCustom
            order={table.order}
            orderBy={table.orderBy}
            headLabel={PRODUCT_HEAD}
            rowCount={data.length}
            numSelected={table.selected.length}
            onSort={table.onSort}
            onRequestSort={table.onSort}
            sx={{ '& th': { py: 1, fontSize: 12 } }}
          />
          <TableBody>
            {paged.map((product, index) => {
              const rank = table.page * table.rowsPerPage + index + 1
              return (
                <TableRow key={product._id} hover sx={{ '& td': { py: 0.75 } }}>
                  <TableCell>
                    <Stack direction='row' alignItems='center' spacing={1.25}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          bgcolor: rank <= 3 ? 'warning.main' : 'grey.200',
                          color: rank <= 3 ? 'white' : 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {rank}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant='body2' fontWeight={600} fontSize={13} noWrap>
                          {product.productName}
                        </Typography>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ fontSize: 11 }}
                        >
                          {fNumber(product.totalOrders)} pedidos
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant='caption'
                      fontWeight={600}
                      color='primary.main'
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 0.75,
                        bgcolor: 'primary.lighter',
                        display: 'inline-block',
                        fontSize: 11,
                      }}
                    >
                      {product.categoryName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2' fontWeight={600} fontSize={13}>
                      {fNumber(product.totalQuantitySold)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant='body2'
                      fontWeight={700}
                      color='success.main'
                      fontSize={13}
                    >
                      {masks.currencyAllPlatforms(product.totalRevenue)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2' fontSize={13}>
                      {masks.currencyAllPlatforms(product.averagePrice)}
                    </Typography>
                  </TableCell>
                </TableRow>
              )
            })}
            <TableEmptyRows
              height={36}
              emptyRows={emptyRows(table.page, table.rowsPerPage, data.length)}
            />
            <TableNoData notFound={!data.length} />
          </TableBody>
        </Table>
      </Scrollbar>
      <TablePaginationCustom
        count={data.length}
        page={table.page}
        rowsPerPage={table.rowsPerPage}
        onPageChange={table.onChangePage}
        onRowsPerPageChange={table.onChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        dense={table.dense}
        onChangeDense={table.onChangeDense}
      />
    </Card>
  )
}

// ----------------------------------------------------------------------
// Vendas por Hora (gráfico combinado: barras = quantidade, linha = R$)
// ----------------------------------------------------------------------

function SalesByHourCard({ data }: { data: any[] }) {
  const hasData = data.some((d: any) => (d.totalQuantitySold || 0) > 0)

  const quantities = data.map((d: any) => d.totalQuantitySold || 0)
  // Valores vêm em centavos do backend - convertemos para reais só para o
  // eixo do gráfico, o formatter depois converte de volta ao usar currencyAllPlatforms.
  const revenues = data.map((d: any) => (d.totalRevenue || 0) / 100)

  const peakHour = useMemo(() => {
    if (!hasData) return null
    let max = -1
    let hour = 0
    data.forEach((d: any) => {
      if ((d.totalQuantitySold || 0) > max) {
        max = d.totalQuantitySold
        hour = d.hour
      }
    })
    return { hour, qty: max }
  }, [data, hasData])

  const chartOptions = useChart({
    chart: {
      stacked: false,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: {
      width: [0, 3],
      curve: 'smooth',
    },
    plotOptions: {
      bar: {
        columnWidth: '55%',
        borderRadius: 4,
        borderRadiusApplication: 'end',
      },
    },
    dataLabels: { enabled: false },
    colors: ['#6366f1', '#10b981'],
    fill: {
      type: ['gradient', 'solid'],
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.2,
        opacityFrom: 0.95,
        opacityTo: 0.75,
      },
    },
    labels: data.map((d: any) => `${String(d.hour).padStart(2, '0')}h`),
    xaxis: {
      labels: { style: { fontSize: '11px', fontWeight: 600 } },
    },
    yaxis: [
      {
        seriesName: 'Quantidade',
        title: {
          text: 'Quantidade vendida',
          style: { fontSize: '12px', fontWeight: 700, color: '#6366f1' },
        },
        labels: {
          style: { colors: '#6366f1', fontSize: '11px', fontWeight: 600 },
          formatter: (val: any) => fShortenNumber(val),
        },
      },
      {
        seriesName: 'Faturamento',
        opposite: true,
        title: {
          text: 'Faturamento (R$)',
          style: { fontSize: '12px', fontWeight: 700, color: '#10b981' },
        },
        labels: {
          style: { colors: '#10b981', fontSize: '11px', fontWeight: 600 },
          formatter: (val: any) => masks.currencyAllPlatforms(val * 100),
        },
      },
    ],
    grid: {
      borderColor: '#e2e8f0',
      strokeDashArray: 3,
      padding: { top: 10, bottom: 5, left: 10, right: 10 },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: [
        { formatter: (val: any) => `${val} unidades` },
        { formatter: (val: any) => masks.currencyAllPlatforms(val * 100) },
      ],
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      fontSize: '13px',
      fontWeight: 600,
      markers: { radius: 6 },
    },
  })

  const series = [
    { name: 'Quantidade', type: 'column', data: quantities },
    { name: 'Faturamento', type: 'line', data: revenues },
  ]

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardHeader
        title={
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <Iconify icon='solar:clock-circle-bold' width={24} />
            <Typography variant='h6' fontWeight={700}>
              Vendas por Horário
            </Typography>
          </Stack>
        }
        subheader={
          peakHour
            ? `Pico às ${String(peakHour.hour).padStart(2, '0')}h (${fNumber(
                peakHour.qty,
              )} unidades) • Horário de Brasília`
            : 'Horário de Brasília'
        }
      />
      <Box sx={{ px: 2, pb: 2 }}>
        {hasData ? (
          <Chart type='line' series={series as any} options={chartOptions} height={340} />
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Iconify
              icon='solar:clock-circle-bold-duotone'
              width={48}
              sx={{ color: 'text.disabled', mb: 1 }}
            />
            <Typography variant='body2' color='text.secondary'>
              Sem vendas no período selecionado
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  )
}

// ----------------------------------------------------------------------
// Heatmap: produto × hora do dia
// Células com intensidade proporcional à quantidade vendida.
// ----------------------------------------------------------------------

function ProductsByHourHeatmap({ data }: { data: any[] }) {
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), [])

  // Valor máximo global (entre todos os produtos e horas) para calibrar a
  // intensidade da cor.
  const maxValue = useMemo(() => {
    let max = 0
    data.forEach((p: any) => {
      p.hourly?.forEach((h: any) => {
        if (h.quantity > max) max = h.quantity
      })
    })
    return max
  }, [data])

  if (!data?.length) {
    return (
      <Card sx={{ borderRadius: 3 }}>
        <CardHeader
          title={
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <Iconify icon='solar:chart-square-bold' width={24} />
              <Typography variant='h6' fontWeight={700}>
                Mapa de Calor: Produtos × Horário
              </Typography>
            </Stack>
          }
        />
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant='body2' color='text.secondary'>
            Sem dados suficientes para gerar o mapa de calor.
          </Typography>
        </Box>
      </Card>
    )
  }

  const getColor = (qty: number) => {
    if (!qty || !maxValue) return 'transparent'
    const intensity = Math.min(qty / maxValue, 1)
    // Escala de verde (mesma paleta da página)
    const alpha = 0.08 + intensity * 0.85
    return `rgba(16, 185, 129, ${alpha})`
  }

  const getTextColor = (qty: number) => {
    if (!qty || !maxValue) return 'text.disabled'
    const intensity = qty / maxValue
    return intensity > 0.55 ? 'white' : 'text.primary'
  }

  const cellSize = 30

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardHeader
        title={
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <Iconify icon='solar:chart-square-bold' width={24} />
            <Typography variant='h6' fontWeight={700}>
              Mapa de Calor: Produtos × Horário
            </Typography>
          </Stack>
        }
        subheader={`Top ${data.length} produtos mais vendidos por hora do dia`}
      />
      <Scrollbar>
        <Box sx={{ p: 2, minWidth: 760 }}>
          {/* Header com as horas */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `240px repeat(24, ${cellSize}px) 80px`,
              gap: '2px',
              alignItems: 'center',
              mb: 0.5,
            }}
          >
            <Box />
            {hours.map((h) => (
              <Typography
                key={h}
                variant='caption'
                sx={{
                  textAlign: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'text.secondary',
                }}
              >
                {String(h).padStart(2, '0')}
              </Typography>
            ))}
            <Typography
              variant='caption'
              sx={{
                textAlign: 'right',
                fontSize: 10,
                fontWeight: 700,
                color: 'text.secondary',
              }}
            >
              TOTAL
            </Typography>
          </Box>

          {/* Linhas do heatmap */}
          {data.map((product: any) => (
            <Box
              key={product._id}
              sx={{
                display: 'grid',
                gridTemplateColumns: `240px repeat(24, ${cellSize}px) 80px`,
                gap: '2px',
                alignItems: 'center',
                mb: '2px',
              }}
            >
              <Tooltip title={product.productName}>
                <Typography
                  variant='body2'
                  fontWeight={600}
                  noWrap
                  sx={{ pr: 1, fontSize: 12 }}
                >
                  {product.productName}
                </Typography>
              </Tooltip>

              {product.hourly.map((h: any) => (
                <Tooltip
                  key={h.hour}
                  title={
                    <Box>
                      <Typography variant='caption' sx={{ fontWeight: 700 }}>
                        {product.productName} — {String(h.hour).padStart(2, '0')}h
                      </Typography>
                      <br />
                      <Typography variant='caption'>
                        {fNumber(h.quantity)} unidades
                      </Typography>
                      <br />
                      <Typography variant='caption'>
                        {masks.currencyAllPlatforms(h.revenue || 0)}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement='top'
                >
                  <Box
                    sx={{
                      height: cellSize,
                      borderRadius: 0.75,
                      bgcolor: getColor(h.quantity),
                      border: '1px solid',
                      borderColor: h.quantity ? 'transparent' : 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: h.quantity ? 'pointer' : 'default',
                      transition: 'transform 0.15s ease',
                      '&:hover': h.quantity
                        ? { transform: 'scale(1.18)', zIndex: 2, boxShadow: 2 }
                        : {},
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: getTextColor(h.quantity),
                      }}
                    >
                      {h.quantity || ''}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}

              <Typography
                variant='body2'
                sx={{ textAlign: 'right', fontWeight: 700, fontSize: 12 }}
              >
                {fNumber(product.grandTotalQuantity)}
              </Typography>
            </Box>
          ))}

          {/* Legenda */}
          <Stack direction='row' alignItems='center' spacing={1} sx={{ mt: 2 }}>
            <Typography variant='caption' color='text.secondary' fontWeight={600}>
              Menos
            </Typography>
            {[0.1, 0.3, 0.5, 0.7, 0.95].map((v) => (
              <Box
                key={v}
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: 0.5,
                  bgcolor: `rgba(16, 185, 129, ${v})`,
                }}
              />
            ))}
            <Typography variant='caption' color='text.secondary' fontWeight={600}>
              Mais
            </Typography>
          </Stack>
        </Box>
      </Scrollbar>
    </Card>
  )
}

function SkeletonOverviewCard() {
  return (
    <Card sx={{ p: 3, borderRadius: 3 }}>
      <Stack direction='row' alignItems='center' spacing={2}>
        <Skeleton variant='rectangular' width={48} height={48} sx={{ borderRadius: 2 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Skeleton variant='text' width={120} height={32} />
          <Skeleton variant='text' width={80} height={20} />
          <Skeleton variant='text' width={100} height={16} />
        </Box>
      </Stack>
    </Card>
  )
}

function SkeletonBigTableCard() {
  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardHeader
        title={
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <Skeleton variant='rectangular' width={24} height={24} />
            <Skeleton variant='text' width={220} height={24} />
          </Stack>
        }
        subheader={<Skeleton variant='text' width={150} height={16} />}
      />
      <Box sx={{ p: 2 }}>
        <Stack
          direction='row'
          spacing={2}
          sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}
        >
          {Array.from(new Array(5)).map((_, index) => (
            <Skeleton key={index} variant='text' width={100} height={20} />
          ))}
        </Stack>
        {Array.from(new Array(6)).map((_, index) => (
          <Stack
            key={index}
            direction='row'
            spacing={2}
            sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Skeleton variant='circular' width={28} height={28} />
            <Skeleton variant='text' width={150} height={20} />
            <Skeleton variant='text' width={80} height={20} />
            <Skeleton variant='text' width={100} height={20} />
            <Skeleton variant='text' width={80} height={20} />
          </Stack>
        ))}
      </Box>
    </Card>
  )
}
